/**
 * Photographed surface maps for the cockpit interior — CC0, from Poly Haven.
 *
 * ## Why these and not more procedural noise
 *
 * `cockpitSurfaces.ts` generates grain into a canvas at start-up, which costs nothing to download
 * and is enough to stop a surface being perfectly uniform. What it cannot do is look like a
 * *material*. Moulded vinyl has a pebble with a size distribution and directional bias that four
 * octaves of value noise does not reproduce; leather has a break pattern; painted alloy has scuffs
 * that follow where hands and boots go. Those are photographs of real surfaces, and there is no
 * procedural shortcut to them that is smaller than the photograph.
 *
 * ## What is loaded, and what deliberately is not
 *
 * **No diffuse maps except the seat.** The cabin's colour is per aircraft — a trainer's grey vinyl,
 * a fighter's grey-green — and comes from `CockpitProfile`. A photographed albedo would overwrite
 * that with whatever colour the sample happened to be, which is also why the frame's maps come
 * from a *blue* metal plate and the blue never reaches the screen: only its normal and roughness
 * are used. The seat is the exception, because real leather's colour variation is most of what
 * makes it read as leather.
 *
 * **Roughness only from the `arm` maps.** Poly Haven packs ambient occlusion, roughness and
 * metalness into one image's R, G and B. three reads `roughnessMap` from the green channel, so the
 * same file serves without unpacking. The red channel is ignored on purpose — occlusion here is
 * baked from *our* geometry (`cockpitAmbient.ts`), which knows about the cabin the surface is in,
 * and a photographed one knows only about the square of leather it was shot from. The blue is
 * ignored because there is no environment map in this scene, so metalness has nothing to reflect.
 *
 * ## Loading, and what happens when it fails
 *
 * Asynchronous and non-blocking. Every material is built with the procedural grain already applied,
 * and a texture that arrives replaces it; a texture that does not arrive leaves the cockpit exactly
 * as it was. That is not defensive padding — it is the offline dev session, the blocked domain and
 * the cold service worker, all of which should degrade to a slightly plainer cockpit rather than to
 * an untextured one or an error overlay.
 *
 * ## Licence
 *
 * CC0, verified from `polyhaven.com/license` rather than assumed: *"All assets … are all licensed
 * as CC0 … You can use our assets for any purpose, including commercial work. You do not need to
 * give credit or attribution."* Credit is given anyway, in `THIRD_PARTY_NOTICES.md` and on the
 * credits screen, because every other asset in this project is credited and a reader should not
 * have to work out which ones needed it.
 *
 * `updateFixed` / `updateFrame`: neither. Requested once when the cockpit is built.
 */

import {
  LinearSRGBColorSpace,
  RepeatWrapping,
  SRGBColorSpace,
  TextureLoader,
  type Texture,
} from 'three';

/** Which surface a map belongs to, and what it is. */
export interface CockpitTextureSet {
  vinylNormal: Texture;
  vinylRoughness: Texture;
  leatherDiffuse: Texture;
  leatherNormal: Texture;
  leatherRoughness: Texture;
  metalNormal: Texture;
  metalRoughness: Texture;
}

const FILES = {
  vinylNormal: 'vinyl_normal.jpg',
  vinylRoughness: 'vinyl_arm.jpg',
  leatherDiffuse: 'leather_diffuse.jpg',
  leatherNormal: 'leather_normal.jpg',
  leatherRoughness: 'leather_arm.jpg',
  metalNormal: 'metal_normal.jpg',
  metalRoughness: 'metal_arm.jpg',
} as const satisfies Record<keyof CockpitTextureSet, string>;

/**
 * Only the diffuse is colour. Everything else is *data*.
 *
 * A normal map or a roughness map run through the sRGB transfer function is a surface that is
 * subtly, inexplicably wrong everywhere — too smooth in the mid-tones and too rough at the ends —
 * and it looks enough like a lighting problem to send you hunting in the wrong file.
 */
const COLOUR_MAPS = new Set<keyof CockpitTextureSet>(['leatherDiffuse']);

let pending: Promise<Partial<CockpitTextureSet>> | null = null;

/**
 * Fetch the set once per session.
 *
 * Shared rather than per cockpit because changing aircraft rebuilds the cockpit, and re-decoding
 * seven JPEGs to get the same seven textures is work with no result.
 */
export function loadCockpitTextures(): Promise<Partial<CockpitTextureSet>> {
  if (pending !== null) return pending;
  const loader = new TextureLoader();
  const base = `${import.meta.env.BASE_URL}textures/cockpit/`;

  pending = Promise.all(
    (Object.keys(FILES) as Array<keyof CockpitTextureSet>).map(
      (key) =>
        new Promise<[keyof CockpitTextureSet, Texture] | null>((resolve) => {
          loader.load(
            `${base}${FILES[key]}`,
            (texture) => {
              texture.wrapS = RepeatWrapping;
              texture.wrapT = RepeatWrapping;
              texture.colorSpace = COLOUR_MAPS.has(key) ? SRGBColorSpace : LinearSRGBColorSpace;
              texture.needsUpdate = true;
              resolve([key, texture]);
            },
            undefined,
            // Resolving rather than rejecting: one missing map should cost that one map, not the
            // whole set. See the header — the cockpit is already usable without any of them.
            () => resolve(null),
          );
        }),
    ),
  ).then((entries) => {
    const set: Partial<CockpitTextureSet> = {};
    for (const entry of entries) {
      if (entry !== null) set[entry[0]] = entry[1];
    }
    return set;
  });

  return pending;
}

/** Drop the cached set. For tests and for a full renderer teardown. */
export function disposeCockpitTextures(): void {
  const settled = pending;
  pending = null;
  void settled?.then((set) => {
    for (const texture of Object.values(set)) texture.dispose();
  });
}
